// Utils
import { parseAllowedRemoteHosts } from "src/utils/emr/allowedRemoteHostsHelper";
import { HUB_AE_TITLE } from "src/utils/emr/pacServerHelper";

// Types
import {
  EMRCatalogDeviceType,
  EMRConnectionType,
  EMRExamType,
  EMRHubConnectionSourceKind,
  EMRPacServerType,
} from "src/services/emr/types";

const FIRST_LISTENER_PORT = 104;
const NEXT_LISTENER_PORT = 11112;
const AE_TITLE_MAX_LENGTH = 16;

const INBOUND_PAC_SERVER_KINDS: Array<EMRHubConnectionSourceKind> = [
  "Worklist",
  "DicomQueryRetrieve",
  "DicomListener",
];

export type HubJobGroups = {
  worklistPacServers: Array<EMRPacServerType>;
  worklists: Array<EMRConnectionType>;
  machines: Array<EMRConnectionType>;
  reportPacServers: Array<EMRPacServerType>;
  reportDestinations: Array<EMRConnectionType>;
};

const hasEnabledJob = (
  pacServer: EMRPacServerType,
  kinds: Array<EMRHubConnectionSourceKind>
) =>
  pacServer.jobs.some((job) => job.enabled && kinds.includes(job.sourceKind));

export const groupHubConnectionsByJob = (
  connections: Array<EMRConnectionType> | undefined,
  pacServers: Array<EMRPacServerType> | undefined
): HubJobGroups => {
  const groups: HubJobGroups = {
    worklistPacServers: [],
    worklists: [],
    machines: [],
    reportPacServers: [],
    reportDestinations: [],
  };

  (pacServers ?? []).forEach((pacServer) => {
    const sendsReports = hasEnabledJob(pacServer, ["DicomStore"]);
    const bringsIn = hasEnabledJob(pacServer, INBOUND_PAC_SERVER_KINDS);

    if (bringsIn || !sendsReports) groups.worklistPacServers.push(pacServer);
    if (sendsReports) groups.reportPacServers.push(pacServer);
  });

  (connections ?? []).forEach((connection) => {
    if (connection.pacServerId) return;

    switch (connection.sourceKind) {
      case "Worklist":
      case "DicomQueryRetrieve":
        groups.worklists.push(connection);
        break;
      case "DicomStore":
      case "ExportFolder":
        groups.reportDestinations.push(connection);
        break;
      default:
        groups.machines.push(connection);
    }
  });

  return groups;
};

export const nextFreeListenerPort = (
  connections: Array<EMRConnectionType> | undefined,
  pacServers: Array<EMRPacServerType> | undefined
) => {
  const usedPorts = new Set(
    [
      ...(connections ?? []),
      ...(pacServers ?? []).flatMap((pacServer) => pacServer.jobs),
    ]
      .filter((connection) => connection.sourceKind === "DicomListener")
      .map((connection) => connection.config?.port)
      .filter((port): port is number => typeof port === "number")
  );

  if (!usedPorts.has(FIRST_LISTENER_PORT)) return String(FIRST_LISTENER_PORT);

  let port = NEXT_LISTENER_PORT;
  while (usedPorts.has(port)) port += 1;
  return String(port);
};

export const hubHasEnabledWorklist = (
  connections: Array<EMRConnectionType> | undefined,
  excludePacServerId?: string
) =>
  (connections ?? []).some(
    (connection) =>
      connection.sourceKind === "Worklist" &&
      connection.enabled &&
      (!excludePacServerId || connection.pacServerId !== excludePacServerId)
  );

export type MachineTransport = "DicomListener" | "SharedFolder";

export type MachineFormValues = {
  name: string;
  manufacturer: string;
  deviceKey: string;
  examTypes: Array<EMRExamType>;
  transport: MachineTransport;
  machineAeTitle: string;
  machineIp: string;
  hubAeTitle: string;
  hubPort: string;
  path: string;
};

export const EMPTY_MACHINE_FORM_VALUES: MachineFormValues = {
  name: "",
  manufacturer: "",
  deviceKey: "",
  examTypes: [],
  transport: "DicomListener",
  machineAeTitle: "",
  machineIp: "",
  hubAeTitle: HUB_AE_TITLE,
  hubPort: String(FIRST_LISTENER_PORT),
  path: "",
};

export const getMachineTransports = (
  device: EMRCatalogDeviceType | undefined
): Array<MachineTransport> =>
  (device?.transports ?? []).filter(
    (transport): transport is MachineTransport =>
      transport === "DicomListener" || transport === "SharedFolder"
  );

const isValidAeTitle = (aeTitle: string) =>
  aeTitle.trim().length > 0 && aeTitle.trim().length <= AE_TITLE_MAX_LENGTH;

export const buildMachineConfig = (
  values: MachineFormValues
): Record<string, unknown> | null => {
  if (values.transport === "SharedFolder") {
    const path = values.path.trim();
    return path ? { path } : null;
  }

  const port = Number(values.hubPort);
  const remoteHosts = parseAllowedRemoteHosts(values.machineIp);

  if (
    values.examTypes.length === 0 ||
    !isValidAeTitle(values.machineAeTitle) ||
    !isValidAeTitle(values.hubAeTitle) ||
    !Number.isInteger(port) ||
    values.hubPort.trim() === "" ||
    port < 1 ||
    port > 65535 ||
    remoteHosts.invalid.length > 0
  ) {
    return null;
  }

  return {
    aeTitle: values.hubAeTitle.trim(),
    port,
    allowedCallingAeTitles: [values.machineAeTitle.trim()],
    ...(remoteHosts.hosts.length > 0 && {
      allowedRemoteHosts: remoteHosts.hosts,
    }),
    examTypes: values.examTypes,
  };
};
